import React from 'react'
import { Typography } from 'antd'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'

import { UserRole } from '@vacationtracker/shared/types/user'
import { splitFullName } from '@vacationtracker/shared/functions/split-full-name'
import IntlMessages from '../../util/IntlMessages'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { getFrontDoorCapabilities, getVeeCapabilities } from './catalogue'
import { getPageSuggestions } from './page-suggestions'
import { VeeSuggestionRow } from './vee-suggestion-row'
import { VeeSendOutcome } from './hooks'

export interface VeeFrontDoorMeta {
  source: 'suggestion'
  group: 'page' | 'capability'
  kbSlug?: string
}

interface IVeeHomeProps {
  onSend: (message: string, meta?: VeeFrontDoorMeta) => Promise<VeeSendOutcome>
}

export const VeeHome: React.FC<IVeeHomeProps> = ({ onSend }) => {
  const { formatMessage } = useIntl()
  const { pathname } = useLocation()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  // Undefined access list means enabled for all roles - same semantics as the backend
  // tool-authorizer and use-energy-score.
  const energyAccess = authCompany?.featurePermissions?.featureVisibility?.energyLevelAccess
  const hasEnergyAccess = !energyAccess || energyAccess.includes(authUser?.role as UserRole)
  const frontDoor = getFrontDoorCapabilities(getVeeCapabilities(hasEnergyAccess))
  const pageSuggestions = getPageSuggestions(pathname, authUser?.role)
  const { firstName } = splitFullName(authUser?.name)

  return (
    <div className="vee-home">
      <div className="vee-home-content">
        <Typography.Title level={4} className="vee-home-greeting">
          {firstName ? formatMessage({ id: 'veeAssistant.home.greeting' }, { name: firstName }) : formatMessage({ id: 'veeAssistant.home.greetingNoName' })}
        </Typography.Title>

        {pageSuggestions.length > 0 && (
          <div className="vee-home-group">
            <Typography.Text type="secondary" className="vee-home-group-label">
              <IntlMessages id="veeAssistant.home.onThisPage" />
            </Typography.Text>
            {pageSuggestions.map((suggestion) => (
              <VeeSuggestionRow
                key={suggestion.titleKey}
                label={formatMessage({ id: suggestion.titleKey })}
                onClick={() =>
                  void onSend(formatMessage({ id: suggestion.askPhraseKey || suggestion.titleKey }), { source: 'suggestion', group: 'page', kbSlug: suggestion.kbSlug })
                }
              />
            ))}
          </div>
        )}

        <div className="vee-home-group">
          <Typography.Text type="secondary" className="vee-home-group-label">
            <IntlMessages id="veeAssistant.home.tryAsking" />
          </Typography.Text>
          {frontDoor.map((capability) => (
            <VeeSuggestionRow
              key={capability.toolName}
              label={formatMessage({ id: capability.homeLabelKey || capability.phraseKey })}
              onClick={() => void onSend(formatMessage({ id: capability.homeLabelKey || capability.phraseKey }), { source: 'suggestion', group: 'capability' })}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
